import { useCallback, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import type { SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { putScenarioMutation } from "@visioni/a3s-traffic-flow/app";
import type { PutScenarioBody } from "@visioni/a3s-traffic-flow/client-messages";
import type { TenantScenarioStatusItem } from "@visioni/a3s-traffic-flow/fleet-messages";

export interface ScenarioConfigItem {
  scenarioUuid: string;
  coordinate?: { lat: number; lng: number };
  direction?: number;
  friendlyName?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export function buildConfigByUuid(items: TenantScenarioStatusItem[] | undefined): Record<string, ScenarioConfigItem> {
  const map: Record<string, ScenarioConfigItem> = {};
  if (!items) return map;
  for (const item of items) {
    for (const scenario of item.scenarios) {
      if (scenario.coordinate || scenario.friendlyName || scenario.direction !== undefined) {
        map[scenario.scenarioUuid] = {
          scenarioUuid: scenario.scenarioUuid,
          coordinate: scenario.coordinate,
          direction: scenario.direction,
          friendlyName: scenario.friendlyName,
          updatedAt: scenario.configUpdatedAt,
          updatedBy: scenario.configUpdatedBy,
        };
      }
    }
  }
  return map;
}

export const usePutScenarioConfig = () => {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const mutationConfig = useMemo(
    () =>
      apiConfig.tenant ?
        {
          tenantId: apiConfig.tenant.tenantId,
          apiBaseUrl: apiConfig.tenant.apiBaseUrl,
          authenticatedFetch: apiConfig.authenticatedFetch,
        }
      : { authenticatedFetch: apiConfig.authenticatedFetch },
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const mutation = useMutation({
    mutationFn: (vars: {
      siteId: SiteId;
      assetCameraKey: AssetCameraKey;
      scenarioUuid: string;
      body: PutScenarioBody;
    }) => {
      const { mutationFn } = putScenarioMutation(mutationConfig, {
        siteId: vars.siteId,
        assetCameraKey: vars.assetCameraKey,
        scenarioUuid: vars.scenarioUuid,
      });
      return mutationFn(vars.body);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        predicate: (q) => {
          const head = q.queryKey[0];
          if (typeof head !== "object" || head === null) return false;
          return (head as { resource?: string }).resource === "scenarios";
        },
      });
    },
  });

  const mutateAsyncRef = useRef(mutation.mutateAsync);
  mutateAsyncRef.current = mutation.mutateAsync;

  const putConfig = useCallback(
    (siteId: SiteId, assetCameraKey: AssetCameraKey, scenarioUuid: string, body: PutScenarioBody) =>
      mutateAsyncRef.current({ siteId, assetCameraKey, scenarioUuid, body }),
    [],
  );

  return {
    putConfig,
    isPending: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error : null,
  };
};
